import { bind } from 'bind-event-listener';

type ExponentialRetryOptions<T> = {
	/**
	 * The function to retry. Receives a `resetRetries` callback that can be called
	 * to reset the retry counter and delay — useful when a partial success occurs
	 * (e.g. a connection was established) and subsequent failures should be treated
	 * as a fresh start rather than consuming the remaining retry budget.
	 */
	fn: (resetRetries: () => void) => Promise<T>;
	maxRetries?: number;
	retryCount?: number;
	delay?: number;
	/**
	 * Controls jitter applied to the exponential delay:
	 * - `false` (default): no jitter, uses exact exponential delay.
	 * - `true`: multiplicative jitter — random between minDelay and computed delay.
	 * - `number`: additive jitter — computed delay + random(0, number) ms.
	 */
	jitter?: boolean | number;
	/** Minimum delay in milliseconds before retry (default: 500) */
	minDelay?: number;
	/** When true, pauses retries while the browser is offline and resumes when connectivity returns */
	pauseWhileOffline?: boolean;
	/** Additional delay in milliseconds to wait after coming back online before retrying (default: 0).
	 *  Useful to account for VPN re-establishment or DNS resolution delays after a network drop. */
	postOnlineDelay?: number;
	/** Predicate which determines if the error should be retried */
	errorFilter?: (error: any) => boolean;
	/** AbortSignal to cancel the retry during backoff wait */
	signal?: AbortSignal;
	/** Callback invoked before each retry attempt (not called for the initial attempt) */
	onRetry?: (attempt: number, error: any) => void;
};

/**
 * Abort-aware sleep that resolves after `ms` or rejects early if the signal is aborted.
 * Falls back to a regular setTimeout when no signal is provided.
 */
const abortableSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}

		let cleanup: () => void = () => {};
		const timer = setTimeout(() => {
			cleanup();
			resolve();
		}, ms);

		if (signal) {
			const onAbort = () => {
				clearTimeout(timer);
				cleanup();
				reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			};
			cleanup = bind(signal, {
				type: 'abort',
				listener: onAbort,
				options: { once: true },
			});
		}
	});

/**
 * Resolves when the browser reports it is online.
 * Returns immediately if already online, otherwise waits for the 'online' event.
 * Rejects if the signal is aborted while waiting.
 * @returns true if it had to wait for the 'online' event, false if already online.
 * Useful to distinguish between "was offline and recovered" vs "was already online".
 */
const waitForOnline = (signal?: AbortSignal): Promise<boolean> => {
	if (typeof navigator === 'undefined' || navigator.onLine) {
		return Promise.resolve(false);
	}

	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}

		const onOnline = () => {
			cleanup();
			resolve(true);
		};
		const onAbort = () => {
			cleanup();
			reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
		};

		const unbindOnline = bind(window, {
			type: 'online',
			listener: onOnline,
			options: { once: true },
		});

		const unbindAbort = signal
			? bind(signal, {
					type: 'abort',
					listener: onAbort,
					options: { once: true },
				})
			: () => {};

		const cleanup = () => {
			unbindOnline();
			unbindAbort();
		};
	});
};

/** Retry the function using exponential backoff */
export const exponentialRetry = async <T>({
	fn,
	maxRetries = 3,
	retryCount = 0,
	delay = 1000,
	jitter = false,
	minDelay = 500,
	pauseWhileOffline = false,
	postOnlineDelay = 0,
	errorFilter,
	signal,
	onRetry,
}: ExponentialRetryOptions<T>): Promise<T> => {
	// Mutable counter shared across recursive calls so that resetRetries() can
	// reset it from within fn() without needing to re-enter the call stack.
	const counter = { retryCount, delay };

	const resetRetries = () => {
		counter.retryCount = 0;
		counter.delay = delay;
	};

	const attempt = async (): Promise<T> => {
		try {
			return await fn(resetRetries);
		} catch (error) {
			if (errorFilter && !errorFilter(error)) {
				throw error;
			}

			if (counter.retryCount >= maxRetries) {
				throw error;
			}

			onRetry?.(counter.retryCount + 1, error);

			if (pauseWhileOffline) {
				const recoveredFromOffline = await waitForOnline(signal);
				if (recoveredFromOffline && postOnlineDelay > 0) {
					await abortableSleep(postOnlineDelay, signal);
				}
			}

			const effectiveDelay =
				typeof jitter === 'number'
					? counter.delay + Math.random() * jitter
					: jitter
						? Math.max(minDelay, Math.random() * counter.delay)
						: counter.delay;
			await abortableSleep(effectiveDelay, signal);

			counter.delay *= 2;
			counter.retryCount += 1;

			return attempt();
		}
	};

	return attempt();
};
